import React from "react";
import { Box, Typography, alpha } from "@mui/material";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceArea,
  ReferenceLine,
} from "recharts";
import { ShowChart as ShowChartIcon } from "@mui/icons-material";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import { useClinicTheme } from "../../../theme";
import EmptyState from "../../atoms/EmptyState";

const AXIS_TICK = { fontSize: 12, fill: COLORS.slate500 };
const fmtDay = (v) => new Date(v).toLocaleDateString("tr-TR", { day: "numeric", month: "short" });
const fmtFull = (v) =>
  new Date(v).toLocaleString("tr-TR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });

/**
 * Seri renkleri (design-principles §6): ana seri tema rengi; ek seriler aynı
 * tonun açığı ve nötr gri — kimlik yalnız renge bırakılmaz, lejant yazar.
 */
const seriesColors = (primary) => [primary, alpha(primary, 0.5), COLORS.slate500, COLORS.slate300];

function ChartTip({ active, payload, label, valueKeys, colors }) {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        bgcolor: COLORS.white,
        border: `1px solid ${COLORS.slate200}`,
        borderRadius: 2,
        boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
        px: 1.5,
        py: 1,
        minWidth: 140,
      }}
    >
      <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500, mb: 0.5 }}>{fmtFull(label)}</Typography>
      {valueKeys.map((vk, i) => {
        const p = payload.find((x) => x.dataKey === vk.key);
        if (!p || p.value == null) return null;
        return (
          <Box key={vk.key} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 10, height: 2, borderRadius: 1, bgcolor: colors[i % colors.length] }} />
            <Typography sx={{ fontSize: TYPO.body, fontWeight: 700, color: COLORS.slate800 }}>
              {p.value} {vk.unit}
            </Typography>
            {valueKeys.length > 1 ? (
              <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500 }}>{vk.label}</Typography>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * Bir ölçüm metriğinin zaman içindeki trendini çizen çizgi grafik.
 * Noktalara sayı basılmaz (değer tooltip'te); ≥2 seride lejant etiket + son
 * değeri yazar. Yükseklik verilmezse kabını doldurur.
 *
 * @param {Array<{takenAt: string|Date, [key: string]: number}>} data
 *   Zaman-sıralı (eskiden yeniye) veri noktaları.
 * @param {Array<{key: string, label: string, unit: string}>} valueKeys
 * @param {(payload: object) => void} [onPointClick]
 *   Verilirse noktalar tıklanabilir olur; tıklanan noktanın veri objesi geçilir.
 * @param {number|string} [height="100%"]
 * @param {{from: number, to: number}} [band] - Normal aralık (açık yeşil alan)
 * @param {Array<{y: number, label: string}>} [lines] - Sınır çizgileri (düz, soluk)
 * @param {(point: object) => string|null} [pointColor] - Sınır dışı noktanın rengi;
 *   yalnız ilk (ana) seride uygulanır
 */
export default function MetricTrendChart({
  data = [],
  valueKeys = [],
  onPointClick,
  height = "100%",
  band,
  lines = [],
  pointColor,
}) {
  const { palette } = useClinicTheme();
  const colors = seriesColors(palette?.primary || COLORS.slate700);

  if (data.length === 0) {
    return (
      <EmptyState
        variant="illustrated-sm"
        icon={<ShowChartIcon />}
        title="Henüz veri yok"
        sx={{ height, py: 0 }}
      />
    );
  }

  const lastOf = (key) => {
    for (let i = data.length - 1; i >= 0; i -= 1) if (data[i][key] != null) return data[i][key];
    return null;
  };

  return (
    <Box sx={{ height, display: "flex", flexDirection: "column", minHeight: 0 }}>
      {valueKeys.length > 1 ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 1 }}>
          {valueKeys.map((vk, i) => {
            const last = lastOf(vk.key);
            return (
              <Box key={vk.key} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box sx={{ width: 12, height: 3, borderRadius: 1, bgcolor: colors[i % colors.length] }} />
                <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate600 }}>
                  {vk.label}
                  {last != null ? (
                    <Box component="span" sx={{ fontWeight: 600, color: COLORS.slate800, ml: 0.5 }}>
                      · son {last} {vk.unit}
                    </Box>
                  ) : null}
                </Typography>
              </Box>
            );
          })}
        </Box>
      ) : null}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
            <CartesianGrid stroke={COLORS.slate100} vertical={false} />
            <XAxis
              dataKey="takenAt"
              tick={AXIS_TICK}
              axisLine={{ stroke: COLORS.slate200 }}
              tickLine={false}
              tickFormatter={fmtDay}
              minTickGap={24}
            />
            <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} width={44} domain={["auto", "auto"]} />
            {band ? (
              <ReferenceArea
                y1={band.from}
                y2={band.to}
                fill={COLORS.success}
                fillOpacity={0.07}
                stroke="none"
                ifOverflow="extendDomain"
              />
            ) : null}
            {lines.map((l) => (
              <ReferenceLine
                key={l.y}
                y={l.y}
                stroke={COLORS.slate300}
                ifOverflow="extendDomain"
                label={{ value: l.label, position: "insideTopRight", fontSize: 11, fill: COLORS.slate500 }}
              />
            ))}
            <Tooltip
              cursor={{ stroke: COLORS.slate200 }}
              content={<ChartTip valueKeys={valueKeys} colors={colors} />}
            />
            {valueKeys.map((vk, i) => {
              const color = colors[i % colors.length];
              // "dot" ve "activeDot" aynı render'ı kullanır: hover sırasında da
              // tıklama aynı noktaya gider. Görünür nokta küçük, tıklama alanı geniş.
              const renderDot = (dotProps) => {
                const { cx, cy, payload, index } = dotProps;
                if (cx == null || cy == null || payload[vk.key] == null) return null;
                const flagged = i === 0 && pointColor ? pointColor(payload) : null;
                const c = flagged || color;
                return (
                  <g
                    key={`dot-${vk.key}-${index}`}
                    style={{ cursor: onPointClick ? "pointer" : "default" }}
                    onClick={onPointClick ? () => onPointClick(payload) : undefined}
                  >
                    {onPointClick && <circle cx={cx} cy={cy} r={10} fill="transparent" />}
                    <circle
                      cx={cx}
                      cy={cy}
                      r={flagged ? 4 : 3}
                      fill={flagged ? c : COLORS.white}
                      stroke={c}
                      strokeWidth={2}
                    />
                  </g>
                );
              };
              return (
                <Line
                  key={vk.key}
                  type="monotone"
                  dataKey={vk.key}
                  name={vk.label}
                  stroke={color}
                  strokeWidth={2}
                  connectNulls
                  dot={renderDot}
                  activeDot={renderDot}
                  isAnimationActive={false}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
